import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const CommunityGuidelinesSection = () => {
  const [expandedGuideline, setExpandedGuideline] = useState(null);

  const guidelines = [
    {
      id: 'respect',
      title: 'Respect & Courtesy',
      icon: 'Heart',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      summary: 'Treat all members with respect and kindness',
      details: [
        'Be respectful in all interactions, even if you\'re not interested',
        'No harassment, bullying, or discriminatory behavior',
        'Respect boundaries when someone says no',
        'Use appropriate language in messages and profiles'
      ]
    },
    {
      id: 'authenticity',
      title: 'Authenticity & Honesty',
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10',
      summary: 'Be genuine and truthful in your profile and interactions',
      details: [
        'Use real, recent photos of yourself',
        'Provide accurate information in your profile',
        'Don\'t impersonate others or create fake profiles',
        'Be honest about your intentions and relationship status'
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Safety',
      icon: 'Shield',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      summary: 'Protect your privacy and respect others\' boundaries',
      details: [
        'Don\'t share personal information too quickly',
        'Never share financial information or send money',
        'Report suspicious behavior immediately',
        'Meet in public places for first dates',
        'Don\'t pressure others to reveal their identity'
      ]
    },
    {
      id: 'content',
      title: 'Appropriate Content',
      icon: 'Image',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      summary: 'Keep all content appropriate and platform-friendly',
      details: [
        'No explicit, sexual, or inappropriate content',
        'No violence, hate speech, or illegal content',
        'Profile photos must be appropriate for all audiences',
        'Messages should be respectful and non-explicit'
      ]
    },
    {
      id: 'legal',
      title: 'Legal Compliance',
      icon: 'Scale',
      color: 'text-error',
      bgColor: 'bg-error/10',
      summary: 'Follow all applicable laws and platform rules',
      details: [
        'You must be 18 years or older to use this platform',
        'No illegal activities or solicitation',
        'Comply with all local laws and regulations',
        'Respect intellectual property rights'
      ]
    }
  ];

  const consequences = [
    { severity: 'Minor Violation', action: 'Warning', icon: 'AlertCircle', color: 'text-warning' },
    { severity: 'Moderate Violation', action: 'Temporary Suspension', icon: 'Clock', color: 'text-warning' },
    { severity: 'Serious Violation', action: 'Account Suspension', icon: 'Ban', color: 'text-error' },
    { severity: 'Severe Violation', action: 'Permanent Ban', icon: 'XCircle', color: 'text-error' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-1">Community Guidelines</h2>
        <p className="text-sm text-muted-foreground">Our standards for a safe and respectful community</p>
      </div>

      {/* Introduction */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex gap-3">
          <Icon name="Info" size={24} className="text-primary mt-0.5" />
          <div>
            <h3 className="font-semibold text-foreground mb-2">Welcome to Our Community</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Our platform is built on trust, respect, and safety. These guidelines help maintain a positive environment for everyone. By using our platform, you agree to follow these rules.
            </p>
            <p className="text-sm text-muted-foreground">
              Violations may result in warnings, suspensions, or permanent bans depending on severity.
            </p>
          </div>
        </div>
      </div>

      {/* Guidelines */}
      <div className="space-y-3">
        {guidelines?.map((guideline) => (
          <div key={guideline?.id} className="bg-card rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setExpandedGuideline(expandedGuideline === guideline?.id ? null : guideline?.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", guideline?.bgColor)}>
                  <Icon name={guideline?.icon} size={24} className={guideline?.color} />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-foreground">{guideline?.title}</h3>
                  <p className="text-sm text-muted-foreground">{guideline?.summary}</p>
                </div>
              </div>
              <Icon
                name="ChevronDown"
                size={20}
                className={cn(
                  "text-muted-foreground transition-transform",
                  expandedGuideline === guideline?.id && "rotate-180"
                )}
              />
            </button>

            {expandedGuideline === guideline?.id && (
              <div className="px-4 pb-4 border-t border-border pt-4">
                <ul className="space-y-2">
                  {guideline?.details?.map((detail, index) => (
                    <li key={index} className="flex gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} className={cn("mt-0.5", guideline?.color)} />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Consequences */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Consequences for Violations</h3>
        <div className="space-y-3">
          {consequences?.map((consequence, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name={consequence?.icon} size={20} className={consequence?.color} />
                <span className="text-sm font-medium text-foreground">{consequence?.severity}</span>
              </div>
              <span className="text-sm text-muted-foreground">{consequence?.action}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reporting */}
      <div className="bg-error/5 border border-error/20 rounded-lg p-6">
        <div className="flex gap-3">
          <Icon name="AlertTriangle" size={24} className="text-error mt-0.5" />
          <div>
            <h3 className="font-semibold text-foreground mb-2">Report Violations</h3>
            <p className="text-sm text-muted-foreground mb-3">
              If you witness behavior that violates these guidelines, please report it immediately. All reports are reviewed by our safety team.
            </p>
            <p className="text-sm text-muted-foreground">
              False reports or abuse of the reporting system may result in action against your account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelinesSection;